import {
    type ConversationStreamState,
    type StreamRunNode,
    type StreamRunState,
} from '@apps/web/components/phoebe_chat/streaming_types';
import {
    isMonitoringToolEntry,
    shouldRenderToolEntryTopLevel,
} from '@apps/web/components/phoebe_chat_view/tool_calls/tool_entry_predicates';
import {
    type RunTraceEntry,
    type ThoughtEntry,
    type ToolAwareItem,
    type ToolExecutionEntry,
    type ToolExecutionLifecycleStatus,
    type TranscriptEntry,
} from '@apps/web/components/phoebe_chat_view/transcript_types';

function getStreamingToolLifecycleStatus({
    isComplete,
    status,
    error,
}: {
    readonly isComplete: boolean;
    readonly status: string | null | undefined;
    readonly error: string | null | undefined;
}): ToolExecutionLifecycleStatus {
    if (!isComplete) {
        return status === 'pending_approval' || status === 'pending_user_input'
            ? 'announced'
            : 'running';
    }
    const normalizedStatus = status?.trim().toLowerCase() ?? '';
    return error ||
        normalizedStatus === 'failed' ||
        normalizedStatus === 'failure' ||
        normalizedStatus === 'incomplete' ||
        normalizedStatus === 'cancelled' ||
        normalizedStatus === 'error'
        ? 'failed'
        : 'completed';
}

function parseJson(value: string | null | undefined): unknown {
    if (!value) {
        return null;
    }
    try {
        return JSON.parse(value);
    } catch {
        return null;
    }
}

function coerceArguments(
    item: ToolAwareItem,
): Record<string, unknown> | Array<unknown> | null {
    if (item.arguments_json && typeof item.arguments_json === 'object') {
        return item.arguments_json as Record<string, unknown> | Array<unknown>;
    }

    const parsed = parseJson(item.text_content);
    if (parsed && typeof parsed === 'object') {
        return parsed as Record<string, unknown> | Array<unknown>;
    }

    return null;
}

function coerceOutput(item: ToolAwareItem): unknown {
    if (item.output_json !== undefined && item.output_json !== null) {
        return item.output_json;
    }

    if (item.raw_json && typeof item.raw_json === 'object') {
        const rawObject = item.raw_json as Record<string, unknown>;
        // Mirror the event-feed payload builder: load_skill outputs carry
        // their display metadata in raw_json.skill_metadata, not output.
        if (item.tool_name === 'load_skill') {
            const skillMetadata = rawObject['skill_metadata'];
            if (
                skillMetadata &&
                typeof skillMetadata === 'object' &&
                !Array.isArray(skillMetadata)
            ) {
                return skillMetadata;
            }
        }
        if ('output' in rawObject) {
            return rawObject['output'];
        }
    }

    const parsed = parseJson(item.text_content);
    if (parsed) {
        return parsed;
    }

    return item.text_content;
}

export function extractReasoning(item: ToolAwareItem): string {
    if (item.text_content && item.text_content.trim()) {
        return item.text_content.trim();
    }

    if (item.raw_json && typeof item.raw_json === 'object') {
        const rawObject = item.raw_json as Record<string, unknown>;
        const thinking = rawObject['thinking'];
        if (typeof thinking === 'string' && thinking.trim().length > 0) {
            return thinking.trim();
        }

        const text = rawObject['text'];
        if (typeof text === 'string' && text.trim().length > 0) {
            return text.trim();
        }

        const summary = rawObject['summary'];
        if (Array.isArray(summary)) {
            const text = summary
                .map((piece) => {
                    if (typeof piece === 'string') {
                        return piece;
                    }
                    if (
                        piece &&
                        typeof piece === 'object' &&
                        'text' in piece &&
                        typeof piece.text === 'string'
                    ) {
                        return piece.text;
                    }
                    return '';
                })
                .filter((segment) => segment && segment.trim().length > 0)
                .join('\n');
            if (text.trim().length > 0) {
                return text;
            }
        }
    }

    return '';
}

export function buildToolExecutionEntry(
    callItem: ToolAwareItem,
    resultItem: ToolAwareItem | undefined,
    callId: string,
    turnSequence?: number,
): ToolExecutionEntry {
    const textValue = resultItem?.text_content
        ? resultItem.text_content.trim()
        : null;
    const outputValue = resultItem ? coerceOutput(resultItem) : undefined;
    const outputForDisplay =
        typeof outputValue === 'string' &&
        textValue !== null &&
        outputValue.trim() === textValue
            ? null
            : outputValue;
    const status = resultItem?.tool_status ?? callItem.tool_status ?? null;
    const isComplete = resultItem !== undefined;

    return {
        kind: 'tool-execution',
        id: callItem.call_id ?? callItem.id,
        toolCallEventId: callItem.id,
        createdAt: callItem.created_at,
        name: callItem.tool_name ?? callId ?? 'Tool execution',
        callId,
        commandId: null,
        status,
        approvalState: null,
        arguments: coerceArguments(callItem),
        output: outputForDisplay,
        text: textValue,
        error: null,
        lifecycleStatus: getStreamingToolLifecycleStatus({
            isComplete,
            status,
            error: null,
        }),
        isComplete,
        durationMs: null,
        turnSequence,
    };
}

export function buildStreamingTranscript(
    streamState: ConversationStreamState,
): Array<TranscriptEntry> {
    if (!streamState.conversationId || !streamState.currentRunId) {
        return [];
    }

    const run = streamState.runsById[streamState.currentRunId];
    if (!run) {
        return [];
    }

    const orderedNodes = run.orderedNodeIds
        .map((nodeId) => run.nodesById[nodeId])
        .filter((node): node is StreamRunNode => node !== undefined);

    const entries: Array<TranscriptEntry> = [];
    const thoughtEntriesById = new Map<string, ThoughtEntry>();
    let latestThoughtEntry: ThoughtEntry | null = null;

    for (const node of orderedNodes) {
        if (node.kind === 'run') {
            continue;
        }

        if (node.kind === 'reasoning') {
            const content = node.content.trim();
            if (content.length === 0) {
                continue;
            }
            const thoughtEntry: ThoughtEntry = {
                kind: 'thought',
                id: node.id,
                createdAt: node.createdAt,
                content,
                tools: [],
                durationMs: node.durationMs ?? null,
            };
            entries.push(thoughtEntry);
            thoughtEntriesById.set(node.id, thoughtEntry);
            latestThoughtEntry = thoughtEntry;
            continue;
        }

        if (node.kind === 'response') {
            const content = node.content.trim();
            if (content.length === 0) {
                continue;
            }
            // Interstitial responses (narration the LLM emits between
            // tool calls) flow into the thinking-trace surface so they
            // read as checkpoints rather than part of the final answer.
            if (node.responseKind === 'interstitial') {
                const thoughtEntry: ThoughtEntry = {
                    kind: 'thought',
                    id: node.id,
                    createdAt: node.createdAt,
                    content,
                    tools: [],
                    durationMs: node.durationMs ?? null,
                };
                entries.push(thoughtEntry);
                thoughtEntriesById.set(node.id, thoughtEntry);
                latestThoughtEntry = thoughtEntry;
                continue;
            }
            if (node.responseKind !== 'final') {
                continue;
            }
            entries.push({
                kind: 'message',
                id: node.id,
                role: 'assistant',
                content,
                createdAt: node.createdAt,
                references: node.references,
            });
            continue;
        }

        const textValue = typeof node.result === 'string' ? node.result : null;
        const outputValue =
            typeof node.result === 'string' ? null : node.result;
        const lifecycleStatus = getStreamingToolLifecycleStatus({
            isComplete: node.isComplete,
            status: node.status,
            error: node.error,
        });
        const toolEntry: ToolExecutionEntry = {
            kind: 'tool-execution',
            id: node.id,
            toolCallEventId: node.toolCallEventId ?? null,
            createdAt: node.createdAt,
            name: node.toolName ?? 'tool_call',
            callId: node.toolCallId,
            commandId: null,
            status: node.status,
            approvalState: null,
            arguments: node.arguments,
            output: outputValue,
            text: textValue,
            error: node.error ?? null,
            lifecycleStatus,
            isComplete: node.isComplete,
            durationMs: node.durationMs ?? null,
        };

        const shouldRenderTopLevel = shouldRenderToolEntryTopLevel(toolEntry);
        const parentThought = node.parentNodeId
            ? (thoughtEntriesById.get(node.parentNodeId) ?? null)
            : latestThoughtEntry;

        if (
            !isMonitoringToolEntry(toolEntry) &&
            !shouldRenderTopLevel &&
            parentThought
        ) {
            parentThought.tools.push(toolEntry);
        } else {
            entries.push(toolEntry);
        }
    }

    return entries;
}

export function buildRunStateFromTraceEntries(
    runId: string,
    entries: Array<RunTraceEntry>,
    status: StreamRunState['status'],
): StreamRunState {
    const nodesById: Record<string, StreamRunNode> = {};
    const orderedNodeIds: Array<string> = [];
    let sequence = 1;

    const pushToolNode = (
        toolEntry: ToolExecutionEntry,
        parentNodeId: string | null,
    ) => {
        const nodeId = toolEntry.id;
        nodesById[nodeId] = {
            id: nodeId,
            parentNodeId,
            kind: 'tool',
            createdAt: toolEntry.createdAt,
            sequence,
            content: '',
            phase: null,
            label: null,
            detail: null,
            toolCallId: toolEntry.callId,
            toolCallEventId: toolEntry.toolCallEventId,
            toolName: toolEntry.name,
            arguments: toolEntry.arguments,
            result: toolEntry.output ?? toolEntry.text,
            error: toolEntry.error ?? null,
            status: toolEntry.status ?? 'completed',
            isComplete: toolEntry.isComplete,
            durationMs: toolEntry.durationMs ?? null,
            responseKind: null,
        };
        orderedNodeIds.push(nodeId);
        sequence += 1;
    };

    for (const entry of entries) {
        if (entry.kind === 'activity') {
            const nodeId = entry.id;
            nodesById[nodeId] = {
                id: nodeId,
                parentNodeId: null,
                kind: 'activity',
                createdAt: entry.createdAt,
                sequence,
                content: '',
                phase: entry.phase,
                label: entry.label,
                detail: entry.detail,
                toolCallId: null,
                toolName: entry.toolName,
                arguments: null,
                result: null,
                error: null,
                status: 'completed',
                isComplete: true,
                durationMs: null,
                responseKind: null,
            };
            orderedNodeIds.push(nodeId);
            sequence += 1;
            continue;
        }

        if (entry.kind === 'message') {
            const nodeId = entry.id;
            nodesById[nodeId] = {
                id: nodeId,
                parentNodeId: null,
                kind: 'response',
                createdAt: entry.createdAt,
                sequence,
                content: entry.content,
                phase: null,
                label: null,
                detail: null,
                toolCallId: null,
                toolName: null,
                arguments: null,
                result: null,
                error: null,
                status: 'completed',
                isComplete: true,
                durationMs: null,
                responseKind: null,
                references: entry.references,
            };
            orderedNodeIds.push(nodeId);
            sequence += 1;
            continue;
        }

        if (entry.kind === 'thought') {
            const nodeId = entry.id;
            nodesById[nodeId] = {
                id: nodeId,
                parentNodeId: null,
                kind: 'reasoning',
                createdAt: entry.createdAt,
                sequence,
                content: entry.content,
                phase: null,
                label: null,
                detail: null,
                toolCallId: null,
                toolName: null,
                arguments: null,
                result: null,
                error: null,
                status: 'completed',
                isComplete: true,
                durationMs: entry.durationMs ?? null,
                responseKind: null,
                references: entry.references,
            };
            orderedNodeIds.push(nodeId);
            sequence += 1;

            for (const toolEntry of entry.tools) {
                pushToolNode(toolEntry, nodeId);
            }
            continue;
        }

        pushToolNode(entry, null);
    }

    return {
        runId,
        requestTurnId: null,
        startedAt: entries[0]?.createdAt ?? null,
        status,
        nodesById,
        orderedNodeIds,
    };
}
